import React from "react";
import styles from "./styles.module.css";

type TagItem = string | { label: string; color?: string; textColor?: string };
type Props = {
  title: string;
  image: string; // 互換性維持のために残す（現在は固定画像を使用）
  tags: TagItem[];
  period?: string;
  description?: string;
  purchaseUrl?: string;
  mapUrl?: string;
  officialUrl?: string;
  /** `officialUrl` があるときは必須（コード側にデフォルト文言はない） */
  officialUrlLabel?: string;
  hashtag?: string; // 未指定なら title を使用
  imageLoading?: "eager" | "lazy";
  imageDecoding?: "async" | "auto" | "sync";
};

const WorkCard: React.FC<Props> = ({
  title,
  image,
  tags,
  period,
  description,
  purchaseUrl,
  mapUrl,
  officialUrl,
  officialUrlLabel,
  hashtag,
  imageLoading = "lazy",
  imageDecoding = "async",
}) => {
  const displayImage = image || "assets/works/works_sakuragawanazo.jpg";
  const hashtagText = hashtag || title;
  const xSearchUrl = `https://x.com/search?q=${encodeURIComponent(
    `#${hashtagText}`
  )}&f=live`;

  const trimmedOfficialUrl = officialUrl?.trim() ?? "";
  const trimmedOfficialLabel = officialUrlLabel?.trim() ?? "";
  const showOfficialLink =
    trimmedOfficialUrl.length > 0 && trimmedOfficialLabel.length > 0;
  const officialLinkText = trimmedOfficialLabel;
  const officialLinkAria =
    officialLinkText.replace(/\s*[＞>]\s*$/, "").trim() || officialLinkText;

  return (
    <article className={styles.card}>
      <div className={styles.imageWrap}>
        <img
          src={displayImage}
          alt={title}
          className={styles.image}
          loading={imageLoading}
          decoding={imageDecoding as any}
        />
      </div>
      {showOfficialLink && (
        <a
          href={trimmedOfficialUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.officialButton}
          aria-label={officialLinkAria}
        >
          {officialLinkText}
        </a>
      )}
      <div className={styles.inner}>
        <h3 className={styles.title}>{title}</h3>
        {typeof period === "string" && period.trim().length > 0 && (
          <div className={styles.period}>リリース日：{period}</div>
        )}
        {description ? (
          typeof description === "string" && description.includes("<") ? (
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{
                __html: description.replace(/\n/g, "<br>"),
              }}
            />
          ) : (
            <>
              {String(description)
                .split(/\r?\n/)
                .filter((line) => line.trim().length > 0)
                .map((line, idx) => (
                  <p key={idx} className={styles.description}>
                    {line}
                  </p>
                ))}
            </>
          )
        ) : (
          <>
            <p className={styles.description}>内容</p>
            <p className={styles.note}>注釈</p>
          </>
        )}

        {tags && tags.length > 0 && (
          <div className={styles.tags}>
            {tags.map((t) => {
              const key = typeof t === "string" ? t : t.label;
              const bg = typeof t === "string" ? undefined : t.color;
              const fg = typeof t === "string" ? undefined : t.textColor;
              return (
                <span
                  key={key}
                  className={styles.tag}
                  style={{ background: bg, color: fg }}
                >
                  {key}
                </span>
              );
            })}
          </div>
        )}

        <a
          href={xSearchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.xButton}
          aria-label="Xで感想を見る"
        >
          Xで感想を見る ＞
        </a>
      </div>
    </article>
  );
};

export default WorkCard;
